import { ReactElement, useEffect, useState } from 'react'
import { CheckCircle, ChevronLeft, ChevronRight, HelpOutline } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'
import { PanelHeader } from '@ui2/structure'
import { ToggleButton } from '@ui/actions'
import { useControlStore } from '@editorStores'
import GuidedIFCToolbar from './GuidedIFCToolbar'
import GuidedToolbarPortal from './GuidedToolbarPortal'
import { GuidedIFCContext } from './context'
import { GuidedIFCStep } from './types'
import { findNavigableStep } from './utils'

export type { GuidedIFCStep } from './types'

interface Props {
  /** Ordered steps — the stepper is generated from this list, so any number of steps works. */
  steps: GuidedIFCStep[]
  /** Called when the user leaves guided mode (via the toolbar exit button, after confirming). */
  onExit?: () => void
}

/**
 * Guided, multi-step wizard shown in the IFC Import drawer. Renders one page (component) per step
 * with an auto-generated stepper and Back/Next controls, a per-step info toggle in the header, and
 * a compact toolbar (portaled into the scene toolbar slot) for step navigation and exit.
 */
const GuidedIFCDrawer = ({ steps, onExit }: Props): ReactElement => {
  const [activeStep, setActiveStep] = useState(() => Math.max(0, findNavigableStep(steps, 0, 1)))
  const [showInfo, setShowInfo] = useState(false)
  const [exitDialogOpen, setExitDialogOpen] = useState(false)

  const setSelectionMode = useControlStore(state => state.setSelectionMode)

  // Clamp so the wizard stays valid even if the steps list changes length.
  const currentStep = steps.length ? Math.min(activeStep, steps.length - 1) : 0
  const step = steps[currentStep]

  // Activate the current step's scene selection mode on entry (planar for the edit steps, IFC
  // selection everywhere else). Re-runs per step so navigation always resets the mode.
  const stepSelectionMode = step?.selectionMode ?? 'ifc-selection'
  useEffect(() => {
    setSelectionMode(stepSelectionMode)
  }, [currentStep, stepSelectionMode, setSelectionMode])

  // Restore the IFC selection default when leaving guided mode (e.g. exiting from an edit step).
  useEffect(() => () => setSelectionMode('ifc-selection'), [setSelectionMode])

  // Skipped steps (e.g. a satisfied upload step) are excluded from Back/Next navigation.
  const prevStep = findNavigableStep(steps, currentStep - 1, -1)
  const nextStep = findNavigableStep(steps, currentStep + 1, 1)
  const isFirst = prevStep === -1
  const isLast = nextStep === -1

  const goToStep = (index: number) => setActiveStep(Math.min(Math.max(0, index), steps.length - 1))
  const handleBack = () => prevStep !== -1 && goToStep(prevStep)
  const handleNext = () => nextStep !== -1 && goToStep(nextStep)

  const confirmExit = () => {
    setExitDialogOpen(false)
    onExit?.()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <GuidedToolbarPortal>
        <GuidedIFCToolbar
          steps={steps}
          activeStep={currentStep}
          onStepChange={goToStep}
          onExit={() => setExitDialogOpen(true)}
        />
      </GuidedToolbarPortal>

      <PanelHeader
        overline={`Schritt ${currentStep + 1} von ${steps.length}`}
        title={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
            {step?.label ?? `Schritt ${currentStep + 1}`}
            {step?.completed && <CheckCircle color="success" sx={{ fontSize: 18 }} />}
          </Box>
        }
        description={step?.description}
        action={
          step?.info ? (
            <ToggleButton
              hint="Infos zu diesem Schritt anzeigen"
              Icon={HelpOutline}
              selected={showInfo}
              onClick={() => setShowInfo(prev => !prev)}
              data-cy="guided-ifc-info"
            />
          ) : undefined
        }
      />

      <Box
        sx={({ palette }) => ({
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: 1.25,
          backgroundColor: palette.grey[100],
        })}
      >
        {showInfo && step?.info && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setShowInfo(false)}>
            {step.info}
          </Alert>
        )}
        <GuidedIFCContext.Provider value={{ goToNext: handleNext }}>
          {step?.content ?? (
            <Typography color="text.secondary">Keine Schritte vorhanden</Typography>
          )}
        </GuidedIFCContext.Provider>
      </Box>

      <Box
        sx={({ palette }) => ({
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          p: 2,
          borderTop: `1px solid ${palette.divider}`,
        })}
      >
        <Button
          variant="outlined"
          startIcon={<ChevronLeft />}
          onClick={handleBack}
          disabled={isFirst}
          data-cy="guided-ifc-back"
          size="small"
        >
          Zurück
        </Button>
        <Button
          variant="contained"
          endIcon={isLast ? undefined : <ChevronRight />}
          onClick={isLast ? confirmExit : handleNext}
          disabled={steps.length === 0}
          data-cy="guided-ifc-next"
          size="small"
        >
          {isLast ? 'Fertig' : 'Weiter'}
        </Button>
      </Box>

      <Dialog open={exitDialogOpen} onClose={() => setExitDialogOpen(false)}>
        <DialogTitle>Geführten Modus verlassen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Wenn Sie den geführten Modus verlassen, wird der aktuelle Fortschritt nicht gespeichert.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setExitDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            size="small"
            color="error"
            onClick={confirmExit}
            data-cy="guided-ifc-exit-confirm"
          >
            Verlassen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default GuidedIFCDrawer
